import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiCheck,
  FiX,
  FiStar,
  FiZap,
  FiTrendingUp,
  FiTarget,
  FiDollarSign,
  FiCalendar,
  FiBarChart2,
  FiPieChart,
  FiBell,
  FiAward,
  FiBook,
  FiSettings,
  FiActivity,
  FiUsers,
  FiLock,
  FiUnlock,
  FiChevronRight,
  FiGift,
  FiShield,
  FiDownload,
  FiCloud,
  FiRefreshCw,
  FiEye,
  FiHeart,
  FiBrain,
  FiGlobe,
  FiMessageSquare,
  FiSun,
  FiMoon,
  FiWind,
  FiFeather,
} from "react-icons/fi";
import { GiBrainstorm } from "react-icons/gi";
import LNavbar from "../landingPage/LNavbar";

const Pricing = () => {
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [selectedPlan, setSelectedPlan] = useState("pro");
  const [hoveredFeature, setHoveredFeature] = useState(null);

  const plans = [
    {
      id: "free",
      name: "Starter",
      tagline: "Essential tools to begin your journey",
      price: 0,
      description:
        "Perfect for beginners who want to build consistency and track basic progress.",
      period: "Forever free",
      color: "#6B7280",
      gradient: "linear-gradient(135deg, #6B7280, #4B5563)",
      accentGradient: "linear-gradient(135deg, #9CA3AF, #6B7280)",
      popular: false,
      features: [
        {
          icon: <FiTarget />,
          text: "Basic Task Manager",
          description:
            "Create and complete simple tasks. Organize your day with basic to-do lists.",
          included: true,
        },
        {
          icon: <FiCalendar />,
          text: "Habit Tracking",
          description:
            "Build daily routines with all habit types. Track your consistency over time.",
          included: true,
        },
        {
          icon: <FiZap />,
          text: "Habit Streaks",
          description:
            "Watch your streak counter grow as you maintain daily discipline.",
          included: true,
        },
        {
          icon: <FiDollarSign />,
          text: "Basic Finance Logging",
          description:
            "Record income and expenses with simple categorization for basic awareness.",
          included: true,
        },
        {
          icon: <FiBarChart2 />,
          text: "Simple Visual Charts",
          description:
            "Basic charts to visualize your spending patterns and habit frequency.",
          included: true,
        },
        {
          icon: <FiTarget />,
          text: "Goal Setting",
          description:
            "Set simple goals but without detailed tracking or progress analytics.",
          included: false,
        },
        {
          icon: <FiPieChart />,
          text: "Financial Analysis",
          description:
            "Deep insights into where your money goes and smart budgeting tools.",
          included: false,
        },
        {
          icon: <FiBell />,
          text: "Smart Reminders",
          description:
            "AI-powered notifications that adapt to your schedule and behavior patterns.",
          included: false,
        },
        {
          icon: <FiTrendingUp />,
          text: "Growth Analytics",
          description:
            "Comprehensive progress tracking with insights and personalized recommendations.",
          included: false,
        },
        {
          icon: <FiBook />,
          text: "Brain Dump",
          description:
            "Capture ideas, dreams, and notes in your personal digital thought space.",
          included: false,
        },
        {
          icon: <FiMessageSquare />,
          text: "Motivational Quotes",
          description:
            "Personalized encouragement that adapts to your current progress and mood.",
          included: false,
        },
        {
          icon: <FiGlobe />,
          text: "Custom Currency",
          description:
            "Track finances in your local currency with automatic exchange rates.",
          included: false,
        },
        {
          icon: <FiSun />,
          text: "Theme Customization",
          description:
            "Choose light/dark themes and customize colors to match your style.",
          included: false,
        },
        {
          icon: <FiDownload />,
          text: "Data Export",
          description:
            "Export all your data in multiple formats for backup and analysis.",
          included: false,
        },
      ],
      cta: "Start Free Journey",
      highlight: "Begin your productivity journey",
      limitations: "Limited to basic features",
    },
    {
      id: "pro",
      name: "Pro",
      tagline: "Complete life management system",
      price: billingPeriod === "monthly" ? 4.99 : 49.99,
      description:
        "Transform your productivity with AI-powered insights and complete financial mastery.",
      period: billingPeriod === "monthly" ? "per month" : "per year",
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6, #7C3AED, #6D28D9)",
      accentGradient: "linear-gradient(135deg, #A78BFA, #8B5CF6, #7C3AED)",
      popular: true,
      features: [
        {
          icon: <FiTarget />,
          text: "Advanced Task Manager",
          description:
            "Priority-based task management with project organization and deadlines.",
          included: true,
        },
        {
          icon: <FiCalendar />,
          text: "Smart Habit Ecosystem",
          description:
            "AI-powered habit suggestions with streak protection and pattern recognition.",
          included: true,
        },
        {
          icon: <FiZap />,
          text: "Streak Guardian",
          description:
            "Intelligent streak protection with motivational boosts before you break habits.",
          included: true,
        },
        {
          icon: <FiDollarSign />,
          text: "Financial Intelligence",
          description:
            "Complete money mastery with spending analysis, budgets, and forecasting.",
          included: true,
        },
        {
          icon: <FiPieChart />,
          text: "Goal Achievement Engine",
          description:
            "Set, track, and achieve financial and personal goals with smart guidance.",
          included: true,
        },
        {
          icon: <FiBarChart2 />,
          text: "Interactive Analytics",
          description:
            "Beautiful, interactive dashboards that reveal insights about your progress.",
          included: true,
        },
        {
          icon: <FiBell />,
          text: "AI-Powered Reminders",
          description:
            "Smart notifications that learn your patterns and suggest optimal times.",
          included: true,
        },
        {
          icon: <FiTrendingUp />,
          text: "My Growth Portal",
          description:
            "Complete overview of your progress across all areas of your life.",
          included: true,
        },
        {
          icon: <FiBook />,
          text: "Creative Brain Dump",
          description:
            "Capture ideas, dreams, and inspiration in your personal digital notebook.",
          included: true,
        },
        {
          icon: <FiMessageSquare />,
          text: "Personalized Motivation",
          description:
            "Custom quotes and encouragement that evolve with your journey.",
          included: true,
        },
        {
          icon: <FiGlobe />,
          text: "Global Currency Support",
          description:
            "Track finances in any currency with real-time exchange rate updates.",
          included: true,
        },
        {
          icon: <FiSun />,
          text: "Complete Theme Control",
          description:
            "Customize every visual element to create your perfect workspace.",
          included: true,
        },
        {
          icon: <FiDownload />,
          text: "Advanced Data Export",
          description:
            "Export comprehensive reports and raw data for in-depth analysis.",
          included: true,
        },
        {
          icon: <FiShield />,
          text: "Priority Support",
          description:
            "Get help from our experts within 24 hours for any questions.",
          included: true,
        },
      ],
      cta: "Transform Your Life",
      highlight: "Most users achieve 3x better results",
      savings: billingPeriod === "yearly" ? "Save €10 (17% off)" : null,
      valueProp: "Everything you need for life mastery",
    },
  ];

  const getCurrentPlan = () =>
    plans.find((plan) => plan.id === selectedPlan) || plans[1];

  return (
    <PricingContainer>
      <LNavbar />
      {/* Animated Background Elements */}
      <BackgroundGlow />
      <FloatingElements />

      <ContentWrapper>
        <HeaderSection>
          <Tagline>
            <FiZap />
            <span>CHOOSE YOUR TRANSFORMATION</span>
          </Tagline>
          <MainTitle>
            Design Your
            <GradientText>Perfect</GradientText>
            Productivity System
          </MainTitle>
          <Subtitle>
            Join <strong>42,857</strong> people who transformed their habits,
            finances, and lives. Start free, upgrade when you're ready for life
            mastery.
          </Subtitle>

          {/* Social Proof */}
          <SocialProof>
            <ProofItem>
              <FiTrendingUp />
              <span>Avg. 78% improvement in habit consistency</span>
            </ProofItem>
            <ProofItem>
              <FiDollarSign />
              <span>Members save €2,100+ annually on average</span>
            </ProofItem>
            <ProofItem>
              <FiAward />
              <span>Rated 4.9/5 by productivity experts</span>
            </ProofItem>
          </SocialProof>
        </HeaderSection>

        {/* Value Proposition */}
        <ValueProposition>
          <ValueCard>
            <ValueIcon>
              <FiWind />
            </ValueIcon>
            <ValueContent>
              <h3>Free Forever, Really</h3>
              <p>
                Our Starter plan includes essential tools to build life-changing
                habits. No hidden fees, no time limits.
              </p>
            </ValueContent>
          </ValueCard>
          <ValueCard accent>
            <ValueIcon>
              <FiFeather />
            </ValueIcon>
            <ValueContent>
              <h3>Pro = Life Mastery</h3>
              <p>
                For less than your daily coffee, get complete control over
                habits, finances, and personal growth.
              </p>
            </ValueContent>
          </ValueCard>
        </ValueProposition>

        {/* Billing Toggle with Savings Highlight */}
        <BillingSection>
          <BillingHeader>
            <h3>Choose Your Commitment</h3>
            <p>Annual billing includes two months free</p>
          </BillingHeader>
          <BillingToggle>
            <ToggleOption
              active={billingPeriod === "monthly"}
              onClick={() => setBillingPeriod("monthly")}
              whileHover={{ scale: 1.05 }}
            >
              <span>Monthly</span>
              <small>Flexible</small>
            </ToggleOption>

            <ToggleSwitch active={billingPeriod === "yearly"}>
              <TogglePill
                active={billingPeriod === "yearly"}
                onClick={() => setBillingPeriod("yearly")}
                layoutId="togglePill"
              />
            </ToggleSwitch>

            <ToggleOption
              active={billingPeriod === "yearly"}
              onClick={() => setBillingPeriod("yearly")}
              whileHover={{ scale: 1.05 }}
            >
              <span>Yearly</span>
              <small>
                Save 17%
                <SavingsBadge>€10 OFF</SavingsBadge>
              </small>
            </ToggleOption>
          </BillingToggle>
        </BillingSection>

        {/* Plans - Modern Card Design */}
        <PlansContainer>
          {plans.map((plan) => (
            <PlanCardWrapper
              key={plan.id}
              popular={plan.popular}
              onClick={() => setSelectedPlan(plan.id)}
            >
              {plan.popular && (
                <PopularRibbon gradient={plan.gradient}>
                  <FiStar />
                  <span>RECOMMENDED BY 92% OF USERS</span>
                </PopularRibbon>
              )}

              <PlanCardContent selected={selectedPlan === plan.id}>
                <PlanHeader>
                  <PlanBadge gradient={plan.gradient}>{plan.name}</PlanBadge>
                  <PlanPrice>
                    {plan.price === 0 ? (
                      <FreePrice>Free</FreePrice>
                    ) : (
                      <>
                        <PriceAmount>€{plan.price.toFixed(2)}</PriceAmount>
                        <PricePeriod>/{plan.period}</PricePeriod>
                      </>
                    )}
                  </PlanPrice>
                  <PlanTagline>{plan.tagline}</PlanTagline>
                  <PlanDescription>{plan.description}</PlanDescription>
                </PlanHeader>

                <PlanHighlight>
                  <FiTrendingUp />
                  <span>{plan.highlight}</span>
                </PlanHighlight>

                {plan.savings && (
                  <SavingsHighlight>
                    <FiGift />
                    <span>{plan.savings}</span>
                  </SavingsHighlight>
                )}

                {/* Feature Comparison */}
                <FeatureComparison>
                  <FeatureGrid>
                    {plan.features.map((feature, index) => (
                      <FeatureItem
                        key={index}
                        included={feature.included}
                        onMouseEnter={() => setHoveredFeature(index)}
                        onMouseLeave={() => setHoveredFeature(null)}
                      >
                        <FeatureIcon included={feature.included}>
                          {feature.included ? <FiCheck /> : <FiX />}
                        </FeatureIcon>
                        <FeatureContent>
                          <FeatureTitle>{feature.text}</FeatureTitle>
                          <AnimatePresence>
                            {(hoveredFeature === index || feature.included) && (
                              <FeatureDescription
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                              >
                                {feature.description}
                              </FeatureDescription>
                            )}
                          </AnimatePresence>
                        </FeatureContent>
                      </FeatureItem>
                    ))}
                  </FeatureGrid>
                </FeatureComparison>

                <PlanCTA
                  gradient={plan.gradient}
                  selected={selectedPlan === plan.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>{plan.cta}</span>
                  <FiChevronRight />
                  {plan.popular && <CTABadge>Most Popular Choice</CTABadge>}
                </PlanCTA>
              </PlanCardContent>
            </PlanCardWrapper>
          ))}
        </PlansContainer>

        {/* Pro Value Highlight */}
        <ProValueSection>
          <ProValueContent>
            <ProValueIcon>
              <GiBrainstorm />
            </ProValueIcon>
            <ProValueText>
              <h2>Why 92% Choose Pro</h2>
              <p>
                For <strong>just €4.99/month</strong> (less than 17¢ per day),
                you get complete life management. That's cheaper than most
                productivity apps but includes everything: habits, finances,
                goals, analytics, and your personal brain dump space.
              </p>
              <ValueMetrics>
                <Metric>
                  <strong>78%</strong>
                  <span>Better habit consistency</span>
                </Metric>
                <Metric>
                  <strong>€2,100+</strong>
                  <span>Average annual savings</span>
                </Metric>
                <Metric>
                  <strong>3.2x</strong>
                  <span>Faster goal achievement</span>
                </Metric>
              </ValueMetrics>
            </ProValueText>
          </ProValueContent>
        </ProValueSection>

        {/* FAQ with Toggle */}
        <FAQSection>
          <FAQHeader>
            <h2>Common Questions</h2>
            <p>Everything you need to make the right choice</p>
          </FAQHeader>
          <FAQGrid>
            <FAQItem>
              <FAQQuestion>
                <span>Can I really achieve my goals with the free plan?</span>
                <FiChevronRight />
              </FAQQuestion>
              <FAQAnswer>
                The free plan helps build consistency, but the Pro plan provides
                the complete system for achieving significant life changes
                through advanced tracking, analytics, and smart guidance.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>
                <span>What makes Pro worth €4.99/month?</span>
                <FiChevronRight />
              </FAQQuestion>
              <FAQAnswer>
                Less than your daily coffee gives you: Complete financial
                analysis, goal achievement system, habit intelligence,
                personalized motivation, and your digital brain dump. Most users
                see a return on investment within the first month through better
                financial decisions.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>
                <span>Can I switch plans later?</span>
                <FiChevronRight />
              </FAQQuestion>
              <FAQAnswer>
                Yes! Start with free, upgrade anytime. All your data transfers
                automatically. If you upgrade to Pro, you get immediate access
                to all advanced features.
              </FAQAnswer>
            </FAQItem>
            <FAQItem>
              <FAQQuestion>
                <span>What if I'm not satisfied with Pro?</span>
                <FiChevronRight />
              </FAQQuestion>
              <FAQAnswer>
                We offer a 30-day money-back guarantee. If Pro doesn't help
                transform your productivity and finances, we'll refund your
                payment, no questions asked.
              </FAQAnswer>
            </FAQItem>
          </FAQGrid>
        </FAQSection>

        {/* Final Decision CTA */}
        {/* <FinalDecision>
          <DecisionContent>
            <DecisionHeader>
              <FiHeart />
              <h2>Ready to Transform Your Life?</h2>
              <p>
                Join thousands who found clarity, consistency, and financial
                freedom. Start your journey today.
              </p>
            </DecisionHeader>
            <DecisionButtons>
              <StartFreeButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedPlan("free")}
              >
                <span>Start Free Forever</span>
                <small>No credit card required</small>
              </StartFreeButton>
              <UpgradeProButton
                gradient={getCurrentPlan().gradient}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedPlan("pro")}
              >
                <span>Start Pro Journey</span>
                <small>
                  {billingPeriod === "monthly" ? "€4.99/month" : "€49.99/year"}
                  <SavingsTag>Save 17%</SavingsTag>
                </small>
              </UpgradeProButton>
            </DecisionButtons>
            <TrustBadges>
              <TrustBadge>
                <FiShield />
                <span>30-day money-back guarantee</span>
              </TrustBadge>
              <TrustBadge>
                <FiCloud />
                <span>Secure cloud backup</span>
              </TrustBadge>
              <TrustBadge>
                <FiUsers />
                <span>42,857+ successful members</span>
              </TrustBadge>
            </TrustBadges>
          </DecisionContent>
        </FinalDecision> */}
      </ContentWrapper>
    </PricingContainer>
  );
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const gradientFlow = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const PricingContainer = styled.section`
  padding: 8rem 5%;
  background: linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 5%;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 50%,
      rgba(139, 92, 246, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(59, 130, 246, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 40% 80%,
      rgba(16, 185, 129, 0.05) 0%,
      transparent 50%
    );
  z-index: 0;
`;

const FloatingElements = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    animation: ${float} 20s ease-in-out infinite;
  }

  &::before {
    width: 500px;
    height: 500px;
    background: rgba(139, 92, 246, 0.1);
    top: 10%;
    left: 10%;
    animation-delay: 0s;
  }

  &::after {
    width: 400px;
    height: 400px;
    background: rgba(59, 130, 246, 0.05);
    bottom: 10%;
    right: 10%;
    animation-delay: 10s;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 5rem;
  animation: ${fadeInUp} 0.8s ease-out;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const Tagline = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.5rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 100px;
  color: #8b5cf6;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 2px;
  margin-bottom: 2rem;
  text-transform: uppercase;

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.75rem;
  }
`;

const MainTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  color: #ffffff;
  margin-bottom: 1.5rem;
  font-family: "Inter", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #8b5cf6, #3b82f6, #10b981);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${gradientFlow} 3s ease infinite;
  display: inline-block;
  margin: 0 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  line-height: 1.6;
  color: #94a3b8;
  max-width: 800px;
  margin: 0 auto 2.5rem;
  opacity: 0.9;

  strong {
    color: #ffffff;
    font-weight: 700;
  }

  @media (max-width: 768px) {
    font-size: 1.125rem;
    padding: 0 1rem;
  }
`;

const SocialProof = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
  max-width: 800px;
  margin: 0 auto;

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ProofItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    color: #8b5cf6;
  }
`;

const ValueProposition = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: ${({ accent }) =>
    accent
      ? "linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.05))"
      : "rgba(255, 255, 255, 0.03)"};
  border: 1px solid
    ${({ accent }) =>
      accent ? "rgba(139, 92, 246, 0.2)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 24px;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ accent }) =>
      accent ? "#8B5CF6" : "rgba(255, 255, 255, 0.2)"};
  }
`;

const ValueIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const ValueContent = styled.div`
  flex: 1;

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.75rem;
  }

  p {
    color: #94a3b8;
    line-height: 1.6;
    font-size: 1rem;
  }
`;

const BillingSection = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const BillingHeader = styled.div`
  margin-bottom: 2rem;

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 0.5rem;
  }

  p {
    color: #94a3b8;
    font-size: 1rem;
  }
`;

const BillingToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 0.5rem;
`;

const ToggleOption = styled(motion.button)`
  padding: 1rem 2rem;
  background: ${({ active }) =>
    active ? "rgba(139, 92, 246, 0.2)" : "transparent"};
  border: 1px solid
    ${({ active }) => (active ? "rgba(139, 92, 246, 0.3)" : "transparent")};
  border-radius: 12px;
  color: ${({ active }) => (active ? "#ffffff" : "#94a3b8")};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  min-width: 140px;
  transition: all 0.3s ease;

  span {
    font-size: 1.125rem;
  }

  small {
    font-size: 0.75rem;
    opacity: 0.8;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &:hover {
    color: #ffffff;
  }
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 60px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 100px;
  cursor: pointer;
`;

const TogglePill = styled(motion.div)`
  position: absolute;
  top: 3px;
  left: ${({ active }) => (active ? "calc(100% - 29px)" : "3px")};
  width: 26px;
  height: 26px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  border-radius: 50%;
  transition: left 0.3s ease;
`;

const SavingsBadge = styled.span`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.6875rem;
  font-weight: 700;
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 6rem;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    max-width: 600px;
    margin: 0 auto 6rem;
  }
`;

const PlanCardWrapper = styled.div`
  position: relative;
  cursor: pointer;
`;

const PopularRibbon = styled.div`
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ gradient }) => gradient};
  color: white;
  padding: 0.75rem 2rem;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 10px 30px rgba(139, 92, 246, 0.3);
  z-index: 2;
  white-space: nowrap;

  &::before {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-top: 10px solid
      ${({ gradient }) => gradient.split(",")[1] || "#7C3AED"};
  }
`;

const PlanCardContent = styled.div`
  background: ${({ selected }) =>
    selected
      ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.8))"
      : "rgba(30, 41, 59, 0.5)"};
  border: 2px solid
    ${({ selected }) =>
      selected ? "rgba(139, 92, 246, 0.4)" : "rgba(255, 255, 255, 0.1)"};
  border-radius: 32px;
  padding: 3rem;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    border-color: rgba(139, 92, 246, 0.4);
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const PlanBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1.5rem;
  background: ${({ gradient }) => gradient};
  color: white;
  border-radius: 100px;
  font-size: 0.875rem;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 1.5rem;
`;

const PlanPrice = styled.div`
  margin-bottom: 0.5rem;
`;

const FreePrice = styled.div`
  font-size: 3.5rem;
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
`;

const PriceAmount = styled.span`
  font-size: 3.5rem;
  font-weight: 800;
  color: #ffffff;
  line-height: 1;
`;

const PricePeriod = styled.span`
  font-size: 1rem;
  color: #94a3b8;
  font-weight: 600;
`;

const PlanTagline = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.5rem;
`;

const PlanDescription = styled.p`
  color: #94a3b8;
  line-height: 1.6;
  font-size: 1rem;
`;

const PlanHighlight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 16px;
  color: #8b5cf6;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1.5rem;
`;

const SavingsHighlight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 12px;
  color: #10b981;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 2rem;
`;

const FeatureComparison = styled.div`
  margin-bottom: 3rem;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.5);
    border-radius: 2px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: ${({ included }) =>
    included ? "rgba(139, 92, 246, 0.05)" : "rgba(255, 255, 255, 0.02)"};
  border: 1px solid
    ${({ included }) =>
      included ? "rgba(139, 92, 246, 0.1)" : "rgba(255, 255, 255, 0.05)"};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(139, 92, 246, 0.1);
    transform: translateX(5px);
  }
`;

const FeatureIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ included }) =>
    included
      ? "linear-gradient(135deg, #8B5CF6, #7C3AED)"
      : "rgba(255, 255, 255, 0.1)"};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ included }) => (included ? "#ffffff" : "#94a3b8")};
  font-size: 0.875rem;
  flex-shrink: 0;
  transition: all 0.3s ease;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.div`
  font-weight: 600;
  color: ${({ included }) => (included ? "#ffffff" : "#94a3b8")};
  margin-bottom: 0.25rem;
  font-size: 0.9375rem;
`;

const FeatureDescription = styled(motion.p)`
  color: #94a3b8;
  font-size: 0.8125rem;
  line-height: 1.5;
  margin: 0;
  overflow: hidden;
`;

const PlanCTA = styled(motion.button)`
  width: 100%;
  padding: 1.25rem 2rem;
  background: ${({ gradient, selected }) =>
    selected ? gradient : "rgba(255, 255, 255, 0.05)"};
  border: 2px solid
    ${({ selected }) => (selected ? "transparent" : "rgba(255, 255, 255, 0.1)")};
  border-radius: 16px;
  color: ${({ selected }) => (selected ? "#ffffff" : "#94a3b8")};
  font-size: 1.125rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    color: #ffffff;
    border-color: ${({ selected }) =>
      selected ? "transparent" : "rgba(139, 92, 246, 0.3)"};
    box-shadow: ${({ selected }) =>
      selected
        ? "0 10px 30px rgba(139, 92, 246, 0.4)"
        : "0 10px 20px rgba(0, 0, 0, 0.2)"};
  }
`;

const CTABadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const ProValueSection = styled.div`
  background: linear-gradient(
    135deg,
    rgba(139, 92, 246, 0.1),
    rgba(59, 130, 246, 0.05)
  );
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 32px;
  padding: 3rem;
  margin-bottom: 6rem;
  animation: ${fadeInUp} 0.8s ease-out 0.8s both;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ProValueContent = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;

  @media (max-width: 1024px) {
    flex-direction: column;
    text-align: center;
    gap: 2rem;
  }
`;

const ProValueIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 2rem;
  }
`;

const ProValueText = styled.div`
  flex: 1;

  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  p {
    color: #94a3b8;
    line-height: 1.7;
    font-size: 1.125rem;
    margin-bottom: 2rem;

    strong {
      color: #ffffff;
      font-weight: 700;
    }
  }
`;

const ValueMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Metric = styled.div`
  text-align: center;

  strong {
    display: block;
    font-size: 3rem;
    font-weight: 800;
    background: linear-gradient(135deg, #8b5cf6, #3b82f6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.5rem;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  span {
    color: #94a3b8;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.4;
  }
`;

const FAQSection = styled.div`
  margin-bottom: 6rem;
  animation: ${fadeInUp} 0.8s ease-out 1s both;
`;

const FAQHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: 3rem;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 0.5rem;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    color: #94a3b8;
    font-size: 1.125rem;
  }
`;

const FAQGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FAQItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
    transform: translateY(-3px);
  }
`;

const FAQQuestion = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ffffff;
  font-weight: 600;
  font-size: 1.125rem;
  margin-bottom: 1rem;

  svg {
    color: #8b5cf6;
    transition: transform 0.3s ease;
  }

  ${FAQItem}:hover & svg {
    transform: rotate(90deg);
  }
`;

const FAQAnswer = styled.p`
  color: #94a3b8;
  line-height: 1.6;
  font-size: 0.9375rem;
  margin: 0;
`;

const FinalDecision = styled.div`
  text-align: center;
  animation: ${fadeInUp} 0.8s ease-out 1.2s both;
`;

const DecisionContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const DecisionHeader = styled.div`
  margin-bottom: 3rem;

  svg {
    font-size: 3rem;
    color: #8b5cf6;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 3rem;
    font-weight: 800;
    color: #ffffff;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }

  p {
    color: #94a3b8;
    font-size: 1.125rem;
    line-height: 1.6;
  }
`;

const DecisionButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const StartFreeButton = styled(motion.button)`
  flex: 1;
  padding: 1.5rem 2rem;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  span {
    font-size: 1.25rem;
  }

  small {
    color: #94a3b8;
    font-size: 0.875rem;
    font-weight: 500;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);
    transform: translateY(-3px);
  }
`;

const UpgradeProButton = styled(motion.button)`
  flex: 1;
  padding: 1.5rem 2rem;
  background: ${({ gradient }) => gradient};
  border: none;
  border-radius: 20px;
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;

  span {
    font-size: 1.25rem;
    position: relative;
    z-index: 1;
  }

  small {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.875rem;
    font-weight: 500;
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 20px 40px rgba(139, 92, 246, 0.4);
  }
`;

const SavingsTag = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const TrustBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const TrustBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #94a3b8;
  font-size: 0.875rem;
  font-weight: 500;

  svg {
    color: #8b5cf6;
  }

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
    color: #ffffff;
  }
`;

export default Pricing;
